import type { FC } from 'react'

import { Input, Select, Checkbox, Textarea, StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Disclosure } from '@skytech/ui/Disclosure'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Form, Field, Formik } from 'formik'
import { useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LocalDateTime } from '@/components/LocalDateTime'
import queries from '@/modules/admin/queries'
import adminQueries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { AssetLocationDetails } from '@/resources/asset-locations'
import assetLocationResources from '@/resources/asset-locations'
import { commonQueries } from '@/resources/queries'

import { AssetsSneakPeek } from './AssetsSneakPeek'

export const Profile: FC<{
  assetLocationDetails: AssetLocationDetails
  onGoToAssetTab: () => void
}> = ({ assetLocationDetails, onGoToAssetTab }) => {
  const queryClient = useQueryClient()
  const translate = useTranslation()
  const params = useParams()
  const assetLocationId = Number(params.assetLocationId)

  if (isNaN(assetLocationId)) throw Error(translate('Employee ID is not a number!'))

  const assetLocationMutation = useMutation(assetLocationResources.edit, {
    onSuccess() {
      queryClient.invalidateQueries(commonQueries.assetLocationDetails(assetLocationId))
      queryClient.invalidateQueries(commonQueries.assetLocationList(), { refetchType: 'all' })
    },
  })

  const { data: departmentList, isLoading: isDepartmentListLoading } = useQuery(
    adminQueries.adminDepartmentsList(),
    () => adminResources.getDepartmentsList()
  )

  const { data: addresses, isLoading: isAddressesLoading } = useQuery(
    queries.adminAddresses(),
    adminResources.getAddressList
  )

  const { data: customers } = useQuery(queries.adminCustomers(), adminResources.getCustomers)
  const customerName = customers?.find(
    customer => customer.id === assetLocationDetails.customer.id
  )?.name

  const departmentOptions =
    departmentList
      ?.filter(
        department =>
          department.customer.id === assetLocationDetails.customer.id && !department.inactive
      )
      ?.map(department => ({
        value: department.id,
        label: [department.code, department.name].join(' - '),
      })) || []

  const addressOptions = addresses
    ?.filter(address => address.isGlobal || address.customerName === customerName)
    .map(address => ({
      value: address.id,
      label: `${address.address}, ${address.zip} ${address.city}`,
    }))

  return (
    <div className="profile">
      <div className="static-info !grid-cols-2">
        <div className="fixed-data">
          <h3>
            <Translation id="Location information" />
          </h3>

          <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
            <Translation id="Company" />
          </StyledText>
          <StyledText block colorMode="black" marginBottom={8}>
            {assetLocationDetails.customer.name}
          </StyledText>

          <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
            <Translation id="Created" />
          </StyledText>
          <div className="mb-2 flex items-center gap-1">
            {assetLocationDetails.createdBy && (
              <StyledText block colorMode="black">
                {assetLocationDetails.createdBy.firstName} {assetLocationDetails.createdBy.lastName}
              </StyledText>
            )}
            <StyledText colorMode="gray" size="small" capitalize block>
              <LocalDateTime date={assetLocationDetails.createdDate} />
            </StyledText>
          </div>

          <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
            <Translation id="Last edited" />
          </StyledText>
          <div className="mb-2 flex items-center gap-1">
            {assetLocationDetails.editedBy && (
              <StyledText block colorMode="black">
                {assetLocationDetails.editedBy.firstName} {assetLocationDetails.editedBy.lastName}
              </StyledText>
            )}
            {assetLocationDetails.editedDate && (
              <StyledText colorMode="gray" size="small" capitalize block>
                <LocalDateTime date={assetLocationDetails.editedDate} />
              </StyledText>
            )}
          </div>
        </div>

        <AssetsSneakPeek
          assetLocationId={Number(assetLocationId)}
          onGoToAssetTab={onGoToAssetTab}
        />
      </div>

      <div className="divider" />

      <Formik
        initialValues={{
          name: assetLocationDetails.name || '',
          departmentId: assetLocationDetails.department.id,
          email: assetLocationDetails.email || '',
          hidden: Boolean(assetLocationDetails.hidden),
          accountingCode: assetLocationDetails.accountingCode || '',
          dim1: assetLocationDetails.dim1 || '',
          dim2: assetLocationDetails.dim2 || '',
          dim3: assetLocationDetails.dim3 || '',
          dim4: assetLocationDetails.dim4 || '',
          customerAddressId: assetLocationDetails.customerAddress?.id || '',
          description: assetLocationDetails.description || '',
          code: assetLocationDetails.code,
        }}
        validate={formValues => {
          const errors: Partial<Record<keyof AssetLocationDetails, string>> = {}

          if (!formValues.name) errors.name = translate('This field is mandatory')
          if (!formValues.code) errors.code = translate('This field is mandatory')

          return errors
        }}
        onSubmit={formValues => {
          if (!assetLocationId) return

          assetLocationMutation.mutate({
            id: Number(assetLocationId),
            payload: {
              ...formValues,
              customerAddressId: Number(formValues.customerAddressId),
              customerId: assetLocationDetails.customer.id,
            },
          })
        }}
      >
        <Form>
          <div className="profile-sections-grid">
            <div>
              <Field name="name">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    capitalizeLabel
                    id="name"
                    label={
                      <>
                        <Translation id="Name" />*
                      </>
                    }
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>

              <Field name="code">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    capitalizeLabel
                    id="code"
                    label={
                      <>
                        <Translation id="Code" />*
                      </>
                    }
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>

              <Field name="email">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    id="email"
                    label={<Translation id="Email" />}
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>

              <Field name="accountingCode">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    id="accountingCode"
                    label={<Translation id="Accounting code" />}
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>

              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button isOpen={open} className="justify-center px-0">
                      <Translation id="Show Dim-values" />
                    </Disclosure.Button>

                    <Disclosure.Panel className="p-0">
                      <Field name="dim1">
                        {({ field, meta }: FieldProps) => (
                          <Input
                            isFullWidth
                            id="dim1"
                            label={<Translation id="Dim 1" />}
                            marginBottom={16}
                            error={meta.error}
                            touched={meta.touched}
                            {...field}
                          />
                        )}
                      </Field>
                      <Field name="dim2">
                        {({ field, meta }: FieldProps) => (
                          <Input
                            isFullWidth
                            id="dim2"
                            label={<Translation id="Dim 2" />}
                            marginBottom={16}
                            error={meta.error}
                            touched={meta.touched}
                            {...field}
                          />
                        )}
                      </Field>

                      <Field name="dim3">
                        {({ field, meta }: FieldProps) => (
                          <Input
                            isFullWidth
                            id="dim3"
                            label={<Translation id="Dim 3" />}
                            marginBottom={16}
                            error={meta.error}
                            touched={meta.touched}
                            {...field}
                          />
                        )}
                      </Field>
                      <Field name="dim4">
                        {({ field, meta }: FieldProps) => (
                          <Input
                            isFullWidth
                            id="dim4"
                            label={<Translation id="Dim 4" />}
                            marginBottom={16}
                            error={meta.error}
                            touched={meta.touched}
                            {...field}
                          />
                        )}
                      </Field>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>

            <div>
              <Field name="departmentId">
                {({ field, form }: FieldProps) => (
                  <Select
                    id="departmentId"
                    label={<Translation id="Department" />}
                    options={departmentOptions}
                    marginBottom={16}
                    {...field}
                    isLoading={isDepartmentListLoading}
                    onChange={option => form.setFieldValue(field.name, option?.value)}
                  />
                )}
              </Field>

              <Field name="customerAddressId">
                {({ field, form }: FieldProps) => (
                  <Select
                    id="customerAddressId"
                    capitalizeLabel
                    label={<Translation id="Address" />}
                    options={addressOptions || []}
                    marginBottom={16}
                    {...field}
                    isLoading={isAddressesLoading}
                    onChange={option => form.setFieldValue(field.name, option?.value)}
                  />
                )}
              </Field>
            </div>

            <div>
              <Field name="description">
                {({ field, meta }: FieldProps) => (
                  <Textarea
                    id="description"
                    marginBottom={16}
                    label={<Translation id="Description" />}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>
            </div>
          </div>

          <footer>
            <div className={`is-user-hidden ${assetLocationDetails.hidden ? 'is-hidden' : ''}`}>
              <Field name="isHidden">
                {({ field }: FieldProps) => (
                  <Checkbox
                    id="isHidden"
                    marginTop={0}
                    label={<Translation id="Is user hidden" />}
                    checked={field.value}
                    {...field}
                  />
                )}
              </Field>
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={assetLocationMutation.isLoading}
              isLoading={assetLocationMutation.isLoading}
            >
              <Translation id="save" />
            </Button>
          </footer>
        </Form>
      </Formik>
    </div>
  )
}
